/**
 * Fair clocks for missions 21-32 (10 §1): a timed arrival, abort or search is never a number somebody
 * liked - it is derived from the route the finished map actually offers, the same walk the validator's
 * 55 %-of-the-clock check measures (validate/routes.ts). The goal's `finish` hook sets each rule's
 * `atSeconds` once the map is whole; the archetype asserts hold the rule to the same measure, so a
 * hand-edited or stale timer that a careful player could not meet is a validation fault.
 */

import { findPath, type NavGrid } from '../../sim/astar';
import { buildStatics } from '../../sim/statics';
import { RANGER } from '../../sim/tuning';
import type { MissionDef, ObjectiveRule, TimedEffect } from '../../sim/types';
import type { Point } from '../types';

export type TimedRule = Extract<ObjectiveRule, { type: 'timedEvent' }>;

export const PACE = {
  /** A careful approach - part walked, part crawled, patrols waited out - takes this many times the bare walk... */
  careful: 2,
  /** ...and this long on top: out of the harness, a look at the map. */
  settle: 30,
  /** No arrival is later than this share of the mission clock: there must be time left to get out. */
  clockShare: 0.6,
  /** The asserts' floor: a timer under this many times the bare walk cannot be met without running the whole way. */
  floor: 1.5,
  /** A prisoner crawls out of his camp: his timer is the crawl itself plus this. */
  crawlSlack: 45,
} as const;

/** One nav grid per finished mission: `finish` and the asserts of the same attempt share it. */
const GRIDS = new WeakMap<MissionDef, { grid: NavGrid; props: number }>();

function gridOf(def: MissionDef): NavGrid {
  const cached = GRIDS.get(def);
  if (cached && cached.props === def.props.length) return cached.grid;
  const grid = buildStatics(def, def.props.map((p) => ({ id: p.id, destroyed: false, looted: false }))).nav;
  GRIDS.set(def, { grid, props: def.props.length });
  return grid;
}

/** The free cell nearest a point: targets are solid, and a man "at" one stands beside it. */
function freeNear(grid: NavGrid, p: Point, reach = 7): [number, number] | null {
  const cx = Math.floor(p.x);
  const cz = Math.floor(p.z);
  let best: [number, number] | null = null;
  let bestD = Infinity;
  for (let dz = -reach; dz <= reach; dz++) {
    for (let dx = -reach; dx <= reach; dx++) {
      const [x, z] = [cx + dx, cz + dz];
      if (x < 0 || z < 0 || x >= grid.width || z >= grid.height || grid.blocked[z * grid.width + x] !== 0) continue;
      const d = dx * dx + dz * dz;
      if (d < bestD) {
        bestD = d;
        best = [x, z];
      }
    }
  }
  return best;
}

/** Metres of the shortest walk between two points of the finished map; null when there is none. */
export function routeMetres(def: MissionDef, from: Point, to: Point): number | null {
  const grid = gridOf(def);
  const a = freeNear(grid, from);
  const b = freeNear(grid, to);
  const path = a && b ? findPath(grid, a[0], a[1], b[0], b[1], 200000) : null;
  if (!path) return null;
  let metres = 0;
  for (let i = 1; i < path.length; i++) {
    const p = path[i - 1];
    const q = path[i];
    if (p && q) metres += p[0] !== q[0] && p[1] !== q[1] ? Math.SQRT2 : 1;
  }
  return metres;
}

/** Seconds at a plain walk; a route that cannot be measured is taken as the crow flies and half as far again. */
export function walkSeconds(def: MissionDef, from: Point, to: Point): number {
  const crow = Math.sqrt((from.x - to.x) * (from.x - to.x) + (from.z - to.z) * (from.z - to.z)) * 1.5;
  return (routeMetres(def, from, to) ?? crow) / RANGER.walkSpeed;
}

const round5 = (seconds: number): number => Math.max(5, Math.round(seconds / 5) * 5);

/** When a careful player who set off at touchdown can be in place at `to`. */
export function carefulSeconds(def: MissionDef, from: Point, to: Point, extra = 0): number {
  const wanted = walkSeconds(def, from, to) * PACE.careful + PACE.settle + extra;
  return round5(Math.min(wanted, def.clockSeconds * PACE.clockShare));
}

/** A crawl all the way (mission 32): metres at crawling speed, plus the slack. */
export function crawlSeconds(def: MissionDef, from: Point, to: Point): number {
  const metres = walkSeconds(def, from, to) * RANGER.walkSpeed;
  return round5(Math.min(metres / RANGER.crawlSpeed + PACE.crawlSlack, def.clockSeconds * PACE.clockShare));
}

/** The mission's timed rule with this effect (and wake tag, when several share an effect). */
export function timedRule(def: MissionDef, effect: TimedEffect, wakes?: string): TimedRule | undefined {
  return def.objective.rules.find((r): r is TimedRule =>
    r.type === 'timedEvent' && r.effect === effect && (wakes === undefined || r.wakes === wakes));
}

/** Assert helper: is this rule's time one a man walking from `from` to `to` can meet without running? */
export function fairTimer(def: MissionDef, rule: TimedRule | undefined, from: Point, to: Point): boolean {
  if (!rule) return false;
  const floor = Math.min(walkSeconds(def, from, to) * PACE.floor, def.clockSeconds * PACE.clockShare - 5);
  return rule.atSeconds >= floor && rule.atSeconds <= def.clockSeconds * PACE.clockShare + 5;
}
